import { Languages } from '@shared/constants/languages';
import { Environments } from '@shared/constants/environments';
import configData from './config.json';
import { IConfig } from '../interfaces/IConfig';

const apiGateway = configData.apiGateway;
const environment: Environments = configData.environment as Environments;
const isLocal: boolean =
	environment === Environments.compose ||
	environment === Environments.dev ||
	environment === Environments.development;
// UPDATE TO HTTPS!!!!!
const mobileUrl = isLocal ? 'https://localhost:8080' : configData.desktopUrl.replace(`://`, `://m.`);

const gatewayBase: string = apiGateway.replace(/\/+$/, '');

/**
 * The app and the gateway answer on the same origin, so the two cookie-authenticated endpoints
 * (the Better Auth engine and the token bridge) are not cross-origin requests at all and need
 * nothing from CORS. Never true for a deployed mobile app (`m.host` vs `host`), but it costs
 * nothing to honour and it is the shape a same-origin deployment would take.
 *
 * Compared as PARSED origins: deployed gateway URLs spell the default port out (`…:443/api`) while
 * `window.location.origin` omits it, so a string `startsWith` answered false for every deployed
 * environment — including ones that genuinely were same-origin. `URL` normalises both sides, which
 * is the comparison the browser itself performs.
 */
const isGatewaySameOrigin: boolean = new URL(gatewayBase, window.location.origin).origin === window.location.origin;

/**
 * Cross-origin, but `vite dev` proxies `/api/auth/engine` to the gateway (see `server.proxy` in
 * `vite.config.ts`), which makes both calls same-origin from the browser's point of view. True
 * only while the dev server is the one serving the app — a build has no proxy.
 */
export const isCookieAuthProxiedByDevServer: boolean = !isGatewaySameOrigin && !!import.meta.env.DEV;

/**
 * There is no longer a reachability gate, and mobile is the clearest illustration of why.
 *
 * It USED to require same-origin (natively or via the dev proxy), because the gateway answered
 * `Access-Control-Allow-Origin: *` with no `Access-Control-Allow-Credentials`, which a credentialed
 * request may not accept. A deployed mobile app is NEVER same-origin (`m.<host>` vs `<host>`), so
 * that condition made the deployed engine login permanently unreachable — mobile could never have
 * cut over at all.
 *
 * Auth now answers CORS itself for `/api/auth`, echoing the exact origin with `Allow-Credentials`
 * for every origin on `betterAuth.trustedOrigins` — and `m.<host>` is derived onto that list by the
 * ansible template for exactly this reason. Cross-origin credentialed calls from this app now
 * succeed, which is the whole premise of mobile owning an engine session.
 *
 * What survives is `isCookieAuthProxiedByDevServer`, which is not about reachability but about
 * which base URL the engine is called on — `authEngineUrl.utils.ts` reads it rather than
 * re-deriving it, so the flag and the base URL cannot drift.
 */
export const config: IConfig = {
	...configData,
	defaultLanguage: configData.defaultLanguage as Languages,
	environment,
	apiGateway,
	mobileUrl,
};
